// #521 — a tela de Contas (Accounts.vue) precisa exibir DOIS estados
// independentes (status da conta × status do login do titular) e confirmar
// a ação administrativa de religar o login com um aviso explícito (é um
// OVERRIDE — pode desfazer um bloqueio manual legítimo — e exige NOVO LOGIN
// porque o accountStatus vem gravado no JWT).
//
// Extraído como funções puras (mesma solução aprovada pelo árbitro no card
// #516/#524 — o projeto não tem harness de teste para componentes Vue).

/**
 * @param {{status: string}} account
 * @returns {string}
 */
function describeAccountStatus(account) {
  return account && account.status === 'ACTIVE' ? 'Ativo' : 'Inativo';
}

/**
 * @param {{owner: {id: number, active: boolean}|null}} account
 * @returns {string}
 */
function describeLoginStatus(account) {
  const owner = account && account.owner;
  if (!owner) return 'Sem titular';
  return owner.active ? 'Login ativo' : 'Login bloqueado';
}

/**
 * Botão "Ativar login do titular" só faz sentido quando existe titular E o
 * login dele está bloqueado — não deve aparecer pra reforçar um login já
 * ativo, nem para conta sem titular.
 * @param {{owner: {id: number, active: boolean}|null}} account
 * @returns {boolean}
 */
function canAtivarLoginTitular(account) {
  const owner = account && account.owner;
  return !!owner && owner.active === false;
}

/**
 * Texto do diálogo de confirmação do override. Duas partes obrigatórias
 * pela spec: (1) aviso de que é um override administrativo sem garantia de
 * preservar a origem do bloqueio; (2) aviso de que o cliente precisará
 * fazer NOVO LOGIN (accountStatus fica congelado no JWT antigo).
 * @returns {{title: string, text: string}}
 */
function buildAtivarLoginTitularConfirmMessage() {
  return {
    title: 'Ativar login do titular?',
    text:
      'Isso é um override administrativo: não temos registro de por que o login foi bloqueado, ' +
      'então esta ação pode desfazer um bloqueio manual legítimo. ' +
      'Depois de ativado, o cliente vai precisar fazer um NOVO LOGIN para o acesso valer ' +
      '(o status da conta fica gravado no token da sessão atual dele).',
  };
}

// #521 (Gate Codex, rodada de correção — item 6) — mesmo limite do backend
// (`AccountController.ativarLoginTitular`, `log.log` é varchar(255)).
// Mantido em duplicidade de propósito: front e backend são bundles
// separados, sem import cruzado api/ <-> client/.
var MOTIVO_MAX_LENGTH = 150;

/**
 * Handler puro da confirmação do diálogo "Ativar login do titular" — só
 * chama o service quando o diálogo foi CONFIRMADO. Extraído do componente
 * pra ser testável sem harness de Vue (mesma solução do resto do card):
 * `dialogResult` é o retorno cru do `$swal.fire(...)` (`{isConfirmed,
 * value}`), `ativarLoginTitularFn` é a função de serviço (injetada, não
 * importada aqui, pra não acoplar este util ao axios/serviço real).
 *
 * @param {{isConfirmed: boolean, value?: string}} dialogResult
 * @param {number|string} accountId
 * @param {(id: number|string, motivo: string) => Promise} ativarLoginTitularFn
 * @returns {Promise<boolean>} true se o service FOI chamado (diálogo confirmado)
 */
async function runAtivarLoginTitularConfirm(dialogResult, accountId, ativarLoginTitularFn) {
  if (!dialogResult || !dialogResult.isConfirmed) {
    return false;
  }

  await ativarLoginTitularFn(accountId, dialogResult.value);
  return true;
}

module.exports = {
  describeAccountStatus,
  describeLoginStatus,
  canAtivarLoginTitular,
  buildAtivarLoginTitularConfirmMessage,
  runAtivarLoginTitularConfirm,
  MOTIVO_MAX_LENGTH,
};
